// Generated by ts-to-zod
import { z } from 'zod';
import { MediaType, MessageType } from './enum';
import { orderSchema } from './order-message';
import {
  messageTemplateSchema,
  noIntentTemplateSchema,
  sendMsgTemplateSchema,
} from './template-schema';
import { invoiceSchema } from './invoice-message';
import { paymentSchema } from './payment-schema';
import { invoiceListItemSchema } from './list-message-schema';
import { campaignSchema } from './campaign-schema';
import { interactiveSchema } from './interactive-template-schema';
import { priceListMessageSchema } from './price-list-message-schema';
import { contactTemplateSchema } from './contact-message-schema';
import { messageTagSchema } from './message-tag-schema';

export const actionSchema = z.object({
  uuid: z.string(),
  actionTimetoken: z.union([z.string(), z.number()]),
});

export const actionValueSchema = z.record(z.array(actionSchema));

export const messageTypeSchema = z.nativeEnum(MessageType);

const buyerDetailsSchema = z.object({
  id: z.string(),
  spaceName: z.string(),
  isoLanguageCode: z.string().optional(),
});

export const userSchema = z.object({
  city: z.string().optional().nullable(),
  contentType: z.string().optional().nullable(),
  firstName: z.string().optional().nullable(),
  userType: z.string().optional().nullable(),
  mobile: z.string().optional().nullable(),
  lastName: z.string().optional().nullable(),
  id: z.string().optional(),
  phone: z.string().optional(),
  address: z.string().optional().nullable(),
  subtitle: z.string().optional().nullable(),
  profilePic: z.string().optional().nullable(),
  email: z.string().optional().nullable(),
  fName: z.string().optional(),
  lName: z.string().optional(),
  mbl: z.string().optional(),
  senderUserId: z.string().optional(),
});

export const orderMessageSchema = orderSchema.extend({
  userDetails: userSchema,
});

export const invoiceMessageSchema = invoiceSchema.extend({
  userDetails: userSchema,
  ownerDetails: userSchema,
});

export const paymentMessageSchema = paymentSchema.extend({
  userDetails: userSchema.optional(),
});

export const invoiceListMessageSchema = z.array(invoiceListItemSchema);

export const mediaSchema = z.object({
  _id: z.string(),
  name: z.string().optional(),
  cntType: z.string(),
  type: z.nativeEnum(MediaType).optional(),
  size: z.number().optional(),
  path: z.string().optional(),
  cUrl: z.string().optional(),
  text: z.string().optional(),
  caption: z.string().optional(),
  provider: z.string().optional(),
  waConfigId: z.string().optional(),
});

export const contactSchema = z.object({
  displayName: z.string().optional(),
  vcard: z.string().optional(),
  phoneNumber: z.string().optional(),
});

export const locationSchema = z.object({
  thumbnail: z.string().optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
});

export const textSchema = z.string();

export const msgReferenceSchema = z.object({
  channel: z.string(),
  messageId: z.string(),
});

const groupMemberSchema = z.object({
  mobile: z.string().nullable().optional(),
  waMemberId: z.string().optional(),
  userId: z.string().nullable().optional(),
});

const waGroupChannelSchema = z.object({
  displayPicture: z.string().nullable().optional(),
  groupName: z.string(),
  created: z.boolean(),
  memberProfilePictures: z.array(z.unknown()),
  groupId: z.string(),
  sessionName: z.string(),
  exists: z.boolean(),
  lastActivity: z.string(),
  id: z.string(),
  updateWaGroupChannel: z
    .object({
      groupMembers: z.object({
        updateMembers: z.array(groupMemberSchema),
      }),
      channelId: z.string(),
      workspaceId: z.string(),
    })
    .optional(),
  createWaGroupChannel: z
    .object({
      groupMembers: z.object({
        members: z.array(
          z.object({
            mobile: z.string(),
            waMemberId: z.string(),
          })
        ),
      }),
      waGroupId: z.string(),
      enableWhatsappNotification: z.boolean(),
      description: z.string(),
      channelName: z.string(),
      channelType: z.string(),
      workspaceId: z.string(),
    })
    .optional(),
  platformChannelId: z.string(),
});

const statsEventSchema = z.object({
  dateTime: z.string(),
  description: z.string().optional(),
  type: z.string().optional(),
});

const statsSchema = z.object({
  MESSAGE_DELIVERED: statsEventSchema.optional(),
  MESSAGE_SEEN: statsEventSchema.optional(),
  MESSAGE_SENT: statsEventSchema.optional(),
  error: statsEventSchema.optional(),
});

const payloadIntentSchema = z.union([messageTypeSchema, z.literal('')]);

const msgTemplateSchema = z
  .union([messageTemplateSchema, noIntentTemplateSchema])
  .optional();

const knowledgeFileReferenceSchema = z.object({
  chunk_index: z.number(),
  document_id: z.string(),
  document_title: z.string(),
  file_path: z.string(),
});

const agentMetadataSchema = z.object({
  knowledge_files_referenced: z.array(knowledgeFileReferenceSchema).optional(),
  tools_used: z.array(z.string()).optional(),
});

const groupedMessageStatsItemSchema = z.object({
  contactId: z.string(),
  contactName: z.string(),
  customerId: z.string(),
  groupedMessageTimetoken: z.string(),
  messageId: z.string(),
  stats: statsSchema,
});

export const reactionSchema = z.object({
  emoji: z.string(),
  timetoken: z.string(),
});

export const channelMetadataSchema = z.object({
  issueStatus: z.string().optional(),
  latestMessageTimeToken: z.string(),
  sellerWorkspaceId: z.string().uuid(),
  channel: z.string().uuid(),
  latestMessage: z.string(),
  latestWaMessageTimeToken: z.string(),
  channelId: z.string().uuid(),
});

const messagePayloadSchema = (
  dataSchema: z.ZodTypeAny,
  template: z.ZodTypeAny = msgTemplateSchema
) =>
  z.object({
    medium: z.string().optional(),
    _v: z.number().optional(),
    sId: z.string(),
    cAt: z.union([z.number(), z.string()]).optional(),
    source: z.string().optional(),
    chnType: z.string().optional(),
    text: textSchema.optional(),
    buyerDetails: buyerDetailsSchema.optional(),
    sellerDetails: buyerDetailsSchema.optional(),
    _id: z.string(),
    latestTransactionMessage: z.string().optional(),
    user: userSchema,
    updateType: z.string().optional(),
    cId: z.string().optional(),
    userDetails: userSchema.optional(),
    tags: z.array(messageTagSchema).optional(),
    reaction: reactionSchema.optional(),
    status: z.string().optional(),
    productNotFound: z.boolean().optional(),
    token: z.string().optional(),
    template: template,
    data: dataSchema.nullable().optional(),
    media: z.array(mediaSchema).optional(),
    fileUrl: z.string().nullable().optional(),
    rFor: z.string().optional(),
    isZoey: z.boolean().optional(),
    sType: messageTypeSchema.optional(),
    inviteId: z.string().optional(),
    intent: payloadIntentSchema.optional(),
    conversation: z.object({ id: z.string() }).optional(),
    campaignId: z.string().optional(),
    waConfigId: z.string().optional(),
    waConfig: z
      .object({
        whatsappPhoneNo: z.string(),
        wabaId: z.string().nullable(),
        displayName: z.string(),
        id: z.string(),
        provider: z.string(),
      })
      .optional(),
    waGroup: z
      .object({
        id: z.string(),
        from: z.string(),
        name: z.string(),
        icon: z.string().optional(),
        channel: waGroupChannelSchema.optional(),
        timestamp: z.union([z.number(), z.string()]).optional(),
      })
      .optional(),
    stats: statsSchema.optional(),
    replyData: z
      .object({
        id: z.string(),
        paticipant: z.string(),
        body: z.string(),
        name: z.string(),
      })
      .optional(),
    reply: z
      .object({
        text: z.string(),
      })
      .optional(),
    formAnswerId: z.string().optional(),
    formId: z.string().optional(),
    actionBy: userSchema.optional(),
    contact: contactSchema.optional(),
    location: locationSchema.optional(),
    eventTimestamp: z.string().optional(),
    agent_metadata: agentMetadataSchema.optional(),
    channelMetadata: channelMetadataSchema.optional(),
    parsedMediaText: z.string().optional(),
    groupedMessageStats: z.array(groupedMessageStatsItemSchema).optional(),
    conversationThreadId: z.string().optional(),
    sellerWorkspaceId: z.string().optional(),
  });

export const messagePayload = z.union([
  messagePayloadSchema(orderMessageSchema.partial()).extend({
    type: z.union([
      z.literal(MessageType.ORDER),
      z.literal(MessageType.TYPEBOT),
    ]),
    intent: z.union([z.literal(MessageType.ORDER), z.literal('')]).optional(),
  }),
  messagePayloadSchema(invoiceMessageSchema.partial()).extend({
    type: z.union([
      z.literal(MessageType.INVOICE),
      z.literal(MessageType.TYPEBOT),
    ]),
    intent: z.union([z.literal(MessageType.INVOICE), z.literal('')]).optional(),
  }),
  messagePayloadSchema(paymentMessageSchema.partial()).extend({
    type: z.union([
      z.literal(MessageType.PAYMENT),
      z.literal(MessageType.TYPEBOT),
    ]),
    intent: z.union([z.literal(MessageType.PAYMENT), z.literal('')]).optional(),
  }),
  messagePayloadSchema(priceListMessageSchema).extend({
    type: z.literal(MessageType.PRICE_LIST),
    intent: messageTypeSchema.optional(),
  }),
  messagePayloadSchema(invoiceListMessageSchema).extend({
    type: z.literal(MessageType.INVOICE_LIST),
    intent: messageTypeSchema.optional(),
  }),
  messagePayloadSchema(campaignSchema).extend({
    type: z.union([
      z.literal(MessageType.CAMPAIGN),
      z.literal(MessageType.TYPEBOT),
      z.literal(MessageType.WHATSAPP_TEMPLATE),
    ]),
    intent: z
      .union([z.literal(MessageType.CAMPAIGN), z.literal('')])
      .optional(),
  }),
  messagePayloadSchema(msgReferenceSchema).extend({
    type: z.union([
      z.literal(MessageType.FORWARD),
      z.literal(MessageType.ISSUE),
    ]),
    intent: messageTypeSchema.optional(),
  }),
  messagePayloadSchema(z.undefined(), interactiveSchema).extend({
    type: z.literal(MessageType.INTERACTIVE),
    intent: messageTypeSchema.optional(),
  }),
  messagePayloadSchema(z.undefined(), contactTemplateSchema).extend({
    type: z.literal(MessageType.CONTACTS),
    intent: messageTypeSchema.optional(),
  }),
  messagePayloadSchema(z.undefined(), sendMsgTemplateSchema).extend({
    type: z.literal(MessageType.WHATSAPP_TEMPLATE),
    intent: messageTypeSchema.optional(),
  }),
  messagePayloadSchema(z.undefined()).extend({
    type: messageTypeSchema,
    intent: payloadIntentSchema.optional(),
  }),
]);

export const messageActionSchema = z.record(actionValueSchema);

export const messageEnvelopeSchema = z.object({
  channel: z.string(),
  message: messagePayload,
  timetoken: z.union([z.string(), z.number()]),
  messageType: z.union([z.string(), z.number()]).optional(),
  publisher: z.string().optional(),
  uuid: z.string().optional(),
  actions: messageActionSchema,
  isInternalNote: z.boolean().optional(),
  displayTimetoken: z.union([z.string(), z.number()]).optional(),
});
